import camelize from 'camelize';
import isEmpty from 'lodash/isEmpty';

import {
  BattlecardActions,
  BookmarkActions,
  CoachingActions,
  CopilotActions,
  HighlightActions,
  MeetingActions,
  MeetingTranslationActions,
  RealtimeAssistantActions,
  RealtimeTranscriptFetchActions,
  ScorecardsActions,
  WebsocketActions
} from 'actions/actionTypes';
import { trackSegmentEvents } from 'actions/segment';

import { getCopilotStreamDataJsonContextUuids } from 'helpers/copilot';
import env from 'helpers/env';
import logError from 'helpers/logError';

const getMessageForAction = (store, action, wsAction) => {
  if (
    action.streamType === WebsocketActions.TYPE_COPILOT &&
    wsAction === 'send'
  ) {
    const dataJson = {
      context_type: action.contextType,
      ...(getCopilotStreamDataJsonContextUuids(
        action.contextType,
        action.contextUuid,
        action.additionalContextMentions
      ) || {}),
      ...(action.userPromptUuid && {
        user_prompt_uuid: action.userPromptUuid
      }),
      ...(action.webSearchEnabled ? { tools: ['search_web'] } : {})
    };
    return JSON.stringify({
      msg: 'stream',
      stream: action.streamType,
      user_email: store.getState().user.email,
      db_object: {
        uuid: action.chatUuid,
        entity_key: action.entityKey
      },
      data: {
        message: action.message,
        data_json: dataJson
      }
    });
  }

  return JSON.stringify({
    msg: 'stream',
    stream: action.streamType,
    action: wsAction,
    db_object: {
      uuid: action.uuid,
      entity_key: action.entityKey
    },
    meta: {
      ts: new Date().toISOString(),
      user_email: store.getState().user.email
    }
  });
};

const getOmnisearchSocketPayload = (store, action) => {
  const data = {
    query: action.query,
    request_id: action.requestId
  };
  if (action.searchType) {
    data.search_type = action.searchType;
  }
  return JSON.stringify({
    msg: 'stream',
    stream: WebsocketActions.TYPE_COPILOT,
    user_email: store.getState().user.email,
    message_type: 'omnisearch',
    data
  });
};

const socketMiddleware = () => {
  let socket = null;
  let connecting = false;
  let pending = null;
  let processingSendMessage = false;
  let pendingCopilotMessages = [];
  /** Queued when user triggers omnisearch while WS is not open (e.g. global Copilot before any chat send). */
  let pendingOmnisearchAction = null;

  const onOpen = store => event => {
    connecting = false;

    store.dispatch({
      type: WebsocketActions.CONNECTED,
      host: event.target.url
    });

    if (pending && socket && socket?.readyState === WebSocket.OPEN) {
      pending.forEach(action => {
        socket.send(getMessageForAction(store, action, 'sub'));
        store.dispatch({
          ...action,
          type: WebsocketActions.SUBSCRIBED
        });
      });

      pending = null;
    }

    // Process any pending copilot messages that were queued while socket was unavailable
    if (
      pendingCopilotMessages.length > 0 &&
      socket?.readyState === WebSocket.OPEN
    ) {
      const messagesToProcess = [...pendingCopilotMessages];
      pendingCopilotMessages = [];

      messagesToProcess.forEach(messageAction => {
        store.dispatch({
          type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
          data: {
            uuid: null,
            author: 1,
            message: messageAction.message,
            chatUuid: messageAction.chatUuid,
            created: messageAction.clientCreatedAt,
            ...(messageAction.copilotStandaloneContextPills?.length
              ? {
                  copilotStandaloneContextPills:
                    messageAction.copilotStandaloneContextPills
                }
              : {})
          }
        });

        try {
          socket.send(getMessageForAction(store, messageAction, 'send'));
        } catch (error) {
          logError('Error sending queued copilot message', error);
        }
      });
    }

    if (pendingOmnisearchAction && socket?.readyState === WebSocket.OPEN) {
      const omnisearchAction = pendingOmnisearchAction;
      pendingOmnisearchAction = null;
      try {
        socket.send(getOmnisearchSocketPayload(store, omnisearchAction));
      } catch (error) {
        logError('Error sending queued copilot omnisearch', error);
        store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
      }
    }
  };

  const onClose = store => () => {
    connecting = false;

    if (pendingOmnisearchAction) {
      pendingOmnisearchAction = null;
      store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
    }

    store.dispatch({ type: WebsocketActions.DISCONNECTED });

    socket = null;
  };

  const onMessage = store => event => {
    const payload = camelize(JSON.parse(event.data));

    switch (payload.stream) {
      case WebsocketActions.TYPE_BATTLECARDS:
        if (isEmpty(payload.data)) {
          logError(event.data, payload);
        } else {
          if (payload.data.dataType === 'answer_card') {
            store.dispatch({
              type: BattlecardActions.FETCH_BATTLECARD_SUCCESS,
              card: payload.data
            });

            // Track the event if the battlecard is triggered by the system
            store.dispatch(
              trackSegmentEvents({
                type: 'ANSWER_CARD_AUTO_DISPLAYED'
              })
            );
          }

          if (payload.data.dataType === 'rt_assistant') {
            store.dispatch({
              type: RealtimeAssistantActions.FETCH_REALTIME_ASSISTANT_SUCCESS,
              methodology: payload.data?.topicCovered
            });
          }
        }

        break;

      case WebsocketActions.TYPE_MEETING_REALTIME_TRANSCRIPTIONS:
        if (isEmpty(payload.data)) {
          // Filter out unnecessary messages from sentry logs
          // Do not log if the payload data is an empty object since that is not an error case
          if (payload.data === null || payload.data === undefined) {
            logError(event.data, payload);
          }
        } else if (payload.data.type === 'notes') {
          // Handle the translated notes here
          store.dispatch({
            type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_NOTES_SUCCESS,
            meetingUuid: payload.data.meetingUuid,
            data: payload.data
          });
        } else if (payload.data.type === 'transcript') {
          // Handle the translated transcript here
          store.dispatch({
            type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_TRANSCRIPT_SUCCESS,
            meetingUuid: payload.data.meetingUuid,
            data: payload.data
          });
        } else {
          // Handle real time transcript message here
          store.dispatch({
            type: RealtimeTranscriptFetchActions.FETCH_REALTIME_TRANSCRIPTION_SUCCESS,
            transcript: payload.data
          });
        }

        break;

      case WebsocketActions.TYPE_BOOKMARKS:
        if (isEmpty(payload.data)) {
          logError(event.data, payload);
        } else {
          store.dispatch({
            type: BookmarkActions.FETCH_BOOKMARK_SUCCESS,
            bookmark: payload.data
          });
        }

        break;

      case WebsocketActions.TYPE_MEETING_META:
        if (isEmpty(payload.dbObject)) {
          logError(event.data, payload);
        } else {
          if (payload.data?.scorecardUuid) {
            store.dispatch({
              type: ScorecardsActions.SET_SCORECARD_UUID_TO_FETCH,
              response: payload.data?.scorecardUuid
            });
          }

          if (payload.data?.recommendationUuid) {
            store.dispatch({
              type: CoachingActions.SET_COACHING_RECOMMENDATION_UUID_TO_FETCH,
              response: payload.data
            });
          }

          if (
            payload.data &&
            'title' in payload.data &&
            'summary' in payload.data
          ) {
            store.dispatch({
              type: HighlightActions.SET_HIGHLIGHT_SUMMARIZATION,
              response: payload.data
            });
          }

          store.dispatch({
            type: MeetingActions.FETCH_META_SUCCESS,
            meetingId: payload.dbObject.uuid,
            metadata: payload.data
          });

          store.dispatch({
            type: MeetingActions.SET_ONLINE_USERS,
            meetingUuid: payload.dbObject.uuid,
            onlineUsers: payload.data.subscribedUsers
          });
        }

        break;

      case WebsocketActions.TYPE_COPILOT: {
        const isOmnisearchResponse =
          payload.messageType === 'omnisearch' ||
          (isEmpty(payload.dbObject) &&
            payload.data &&
            (Array.isArray(payload.data.meeting) ||
              Array.isArray(payload.data.deal) ||
              Array.isArray(payload.data.user) ||
              Array.isArray(payload.data.team) ||
              Array.isArray(payload.data.playlist) ||
              Array.isArray(payload.data.company) ||
              Array.isArray(payload.data.account)));
        if (isOmnisearchResponse && payload.data) {
          const companyList =
            payload.data.company !== undefined
              ? payload.data.company
              : payload.data.account;
          store.dispatch({
            type: CopilotActions.RECEIVE_COPILOT_OMNISEARCH,
            data: {
              requestId: payload.data?.requestId,
              meeting: payload.data?.meeting,
              deal: payload.data?.deal,
              user: payload.data?.user,
              team: payload.data?.team,
              playlist: payload.data?.playlist,
              company: companyList,
              /** Relevance order for grouped omnisearch sections (camelized from `context_type_order`). */
              contextTypeOrder: payload.data?.contextTypeOrder
            }
          });
          break;
        }
        if (isEmpty(payload.dbObject)) {
          logError(event.data, payload);
        } else if (payload.msg === 'copilot_name_updated') {
          store.dispatch({
            type: CopilotActions.RECEIVE_COPILOT_NAME_UPDATE,
            data: {
              ...payload.data,
              chatUuid: payload.dbObject.uuid
            }
          });
        } else {
          store.dispatch({
            type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
            data: {
              ...payload.data,
              chatUuid: payload.dbObject.uuid
            }
          });
        }

        break;
      }

      default:
        break;
    }
  };

  const connect = store => {
    connecting = true;

    const host = env.websocketUrl;

    // connect to the remote host
    socket = new WebSocket(host);

    // websocket handlers
    socket.onmessage = onMessage(store);
    socket.onclose = onClose(store);
    socket.onopen = onOpen(store);
  };

  // the middleware part of this function
  return store => next => action => {
    switch (action.type) {
      case WebsocketActions.CONNECT: {
        if (socket !== null) {
          socket.close();
        }

        if (!connecting) {
          connect(store);
        }

        break;
      }

      case WebsocketActions.DISCONNECT:
        if (socket !== null) {
          socket.close();
        }

        socket = null;

        break;

      case WebsocketActions.SUBSCRIBE: {
        const message = getMessageForAction(store, action, 'sub');

        if (socket && socket?.readyState === WebSocket.OPEN) {
          socket.send(message);
          store.dispatch({
            ...action,
            type: WebsocketActions.SUBSCRIBED
          });
        } else {
          if (!connecting) {
            connect(store);
          }

          if (!pending) {
            pending = [];
          }

          pending.push(action);
        }

        break;
      }

      case WebsocketActions.UNSUBSCRIBE: {
        const message = getMessageForAction(store, action, 'unsub');

        if (socket && socket?.readyState === WebSocket.OPEN) {
          socket.send(message);

          store.dispatch({
            ...action,
            type: WebsocketActions.UNSUBSCRIBED
          });
        } else {
          if (!connecting) {
            connect(store);
          }

          if (pending && socket && socket?.readyState === WebSocket.OPEN) {
            // Removing if we have any pending `sub` for same meeting uuid as of `unsub`
            pending = pending.filter(
              pendingAction =>
                !(
                  action &&
                  pendingAction &&
                  action.streamType === pendingAction.streamType &&
                  action.uuid === pendingAction.uuid &&
                  action.entityKey === pendingAction.entityKey
                )
            );
          }
        }

        break;
      }

      case CopilotActions.SEND_COPILOT_MESSAGE: {
        const processCopilotMessage = messageAction => {
          if (!socket || socket?.readyState !== WebSocket.OPEN) {
            // Queue the message if socket is not ready
            pendingCopilotMessages.push(messageAction);
            return;
          }

          processingSendMessage = true;

          try {
            store.dispatch({
              type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
              data: {
                uuid: null,
                author: 1,
                message: messageAction.message,
                chatUuid: messageAction.chatUuid,
                created: messageAction.clientCreatedAt,
                ...(messageAction.copilotStandaloneContextPills?.length
                  ? {
                      copilotStandaloneContextPills:
                        messageAction.copilotStandaloneContextPills
                    }
                  : {})
              }
            });

            socket.send(getMessageForAction(store, messageAction, 'send'));
          } catch (error) {
            logError('Error sending receiving message from copilot', error);
          } finally {
            processingSendMessage = false;

            // Process next queued message if any
            if (pendingCopilotMessages.length > 0) {
              const nextMessage = pendingCopilotMessages.shift();
              processCopilotMessage(nextMessage);
            }
          }
        };

        if (processingSendMessage) {
          // Queue the message if already processing another
          pendingCopilotMessages.push(action);
        } else {
          processCopilotMessage(action);
        }
        break;
      }

      case CopilotActions.SEND_COPILOT_OMNISEARCH: {
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(getOmnisearchSocketPayload(store, action));
          } catch (error) {
            logError('Error sending copilot omnisearch', error);
            return next({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
          }
        } else {
          pendingOmnisearchAction = action;
          if (!connecting) {
            connect(store);
          }
        }
        return next(action);
      }

      case CopilotActions.CLEAR_COPILOT_OMNISEARCH: {
        pendingOmnisearchAction = null;
        return next(action);
      }

      default:
        return next(action);
    }

    return next(action);
  };
};

export default socketMiddleware();
